import * as CL from '@design-system/component-library';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses';
import { confirmMsg, fieldCantBeEmptyMsg, t } from '../../common/i18n';
import { FormGridFieldset } from '../../common/react-hook-form/FormGridFieldset/FormGridFieldset';
import { InputComponent } from '../../common/react-hook-form/components/InputComponent';
import { Icon } from '../Icon/Icon';

const OtpField = () => {
  const validate = (value: string) => {
    if (!value) {
      return t.VPVR(fieldCantBeEmptyMsg);
    }
    return undefined;
  };

  return (
    <InputComponent
      autocomplete="off"
      validate={validate}
      name="otp"
      type="number"
      maxLength={6}
      label={t.F16P('Confirmation code')}
      required={true}
      disabled={false}
    />
  );
};

interface ResendCodeButtonProps {
  validUntil: number;
  onResend: () => void;
}

export const ResendCode = ({ validUntil, onResend }: ResendCodeButtonProps) => {
  const [remainingTime, setRemainingTime] = useState(validUntil - Date.now());
  const [resendNotAllowed, setResendNotAllowed] = useState(false);
  const [codeCreatedTime, setCodeCreatedTime] = useState<number>(Date.now());

  useEffect(() => {
    setCodeCreatedTime(Date.now());
    setResendNotAllowed(false);
  }, [validUntil]);

  useEffect(() => {
    const updateRemainingTime = () => {
      const newRemainingTime = validUntil - Date.now();
      setRemainingTime(newRemainingTime);
      return newRemainingTime;
    };

    if (updateRemainingTime() <= 0) {
      return;
    }

    const intervalId = setInterval(() => {
      if (updateRemainingTime() <= 0) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [validUntil]);

  useEffect(() => {
    if (Date.now() - codeCreatedTime >= 60 * 1000 || remainingTime <= 0) {
      setResendNotAllowed(false);
    }
  }, [codeCreatedTime, remainingTime]);

  const resendCode = () => {
    if (Date.now() - codeCreatedTime >= 60 * 1000 || remainingTime <= 0) {
      onResend();
      return;
    }
    setResendNotAllowed(true);
  };

  const formatRemainingTime = (milliseconds: number): string => {
    const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`${dsClass.MARGIN_TOP_2} of-employee-self-registration__resend-code-container`}>
      {remainingTime > 0 && (
        <span className="of-employee-self-registration__otp-valid-time">{`${t.GDBJ('Code is valid')} ${formatRemainingTime(remainingTime)}`}</span>
      )}
      {resendNotAllowed && (
        <CL.Disclaimer
          text={t.DVRM(`The confirmation code can't be resent yet. Please try again in a moment.`)}
          disclaimerType="warning"
          icon={<CL.Icon icon="notification" color="notice" size="l" />}
        />
      )}
      <CL.Button color="link" size="m" onClick={resendCode}>
        {t.A00T('Resend code')}
      </CL.Button>
    </div>
  );
};

export interface VerifyOtpProps {
  email?: string;
  phoneNumber?: string;
  validUntil: number;
  verifying: boolean;
  expiredOtp: boolean;
  verifyOtp: (otp: string) => void;
  resendOtp: () => Promise<boolean>;
  goBack: () => void;
  headerText?: string;
  descriptionText?: string;
  goBackText?: string;
}

export const VerifyOtp = ({
  email,
  phoneNumber,
  validUntil,
  verifying,
  expiredOtp,
  verifyOtp,
  resendOtp,
  goBack,
  headerText,
  descriptionText,
  goBackText,
}: VerifyOtpProps) => {
  const [resend, setResend] = useState(false);
  const methods = useForm({ mode: 'all' });

  const showInvalidOrExpiredError = useCallback(() => {
    methods.setError('otp', {
      type: 'manual',
      message: t.HD01(
        'The code you provided is invalid or has expired. Check your email or request a new confirmation message.'
      ),
    });
  }, [methods]);

  useEffect(() => {
    if (expiredOtp) {
      showInvalidOrExpiredError();
    }
  }, [expiredOtp, showInvalidOrExpiredError]);

  useEffect(() => {
    if (resend) {
      const timeoutId = setTimeout(() => {
        setResend(false);
      }, 60 * 1000);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [resend]);

  const onSubmit = async (values: { otp: string }) => {
    if (validUntil < Date.now()) {
      showInvalidOrExpiredError();
      return;
    }
    verifyOtp(values.otp);
  };

  const onResendOtp = async () => {
    const success = await resendOtp();
    if (success) {
      setResend(true);
    }
  };

  const defaultHeaderText = email ? t.RXQO('Confirm your email address.') : t.H56I('Confirm your phone number.');

  const defaultGoBackText = email ? t.NIJZ('Change email address') : t.TC3W('Change phone number');

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <FormGridFieldset>
            <h3 className={dsClass.MARGIN_TOP_0}>{headerText ?? defaultHeaderText}</h3>
            {resend && (
              <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER} ${dsClass.MARGIN_BOTTOM_3}`}>
                <Icon icon="check" color="black" />
                <span>{t.VCRE('New verification code sent.')}</span>
              </div>
            )}
            <div className={dsClass.MARGIN_BOTTOM_3}>
              {descriptionText ? (
                <span>{descriptionText}</span>
              ) : (
                <>
                  {email && (
                    <>
                      <span>{t.IGY8('We sent a confirmation code to ')}</span>
                      <b>{email}</b>
                      <span>
                        {t.W402(
                          '. To confirm your email address, enter the code you received in the field below. The code is valid for 10 minutes.'
                        )}
                      </span>
                    </>
                  )}
                  {phoneNumber && (
                    <>
                      <span>{t.JDSQ('We sent a confirmation code via SMS to the number ')}</span>
                      <b>{`*******${phoneNumber.slice(-3)}`}</b>
                      <span>
                        {t.LEDQ(
                          '. To confirm your phone number, enter the code you received in the field below. The code is valid for 10 minutes.'
                        )}
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
            <OtpField />
            <CL.Button type="submit" size="m" loading={verifying}>
              {t.QVYK(confirmMsg)}
            </CL.Button>
            <div>
              <ResendCode validUntil={validUntil} onResend={onResendOtp} />
            </div>
            <div>
              <CL.Button color="link" size="m" onClick={goBack} icon={<Icon icon="arrow-left-alt" color="primary" />}>
                {goBackText ?? defaultGoBackText}
              </CL.Button>
            </div>
          </FormGridFieldset>
        </form>
      </FormProvider>
    </div>
  );
};
